// 'use client' is load-bearing, not decoration: this module calls React hooks,
// and it is reachable from the `@auctogon/ui/web` barrel. A Server Component
// importing ANY symbol from that barrel pulls this file into the RSC graph,
// where hooks don't exist. Guarded by web-barrel-client-directive.test.ts
// (BL-1124).
'use client';

/**
 * useFocusTrap — move focus into an overlay on open, keep Tab inside it, and
 * hand focus back where it came from on close.
 *
 * The one implementation of overlay focus management for the whole design
 * system, per [ADR 030](../../../../docs/adrs/030_DESIGN_SYSTEM_FOUNDATION.md)
 * Decision 2: `packages/ui` core owns behaviour and structure — keyboard
 * handling, ARIA, focus management — while each app's skin binds appearance
 * only. Extracted from `components/Modal.tsx` (BUG-1386) so admin-web's legacy
 * overlay primitives consume it instead of hand-rolling a fourth copy; DOM-bound
 * (`document`, `focus()`), so it ships from the `@auctogon/ui/web` subpath and
 * never from the RN-safe root. Same consolidation shape as `useOutsideClick`.
 *
 * Three properties are load-bearing and are what BUG-1386 was about:
 *
 *  - **The focus hand-off is synchronous.** It happens in the effect, once the
 *    node is committed — never on a timer. The three admin copies this replaced
 *    each waited a guessed `setTimeout(…, 50)` for "the portal to mount", which
 *    nothing coordinated with: it fired ~50ms after open regardless of what the
 *    operator had done in the meantime.
 *  - **It never steals focus that is already inside the overlay.** An operator
 *    fast enough to click a field before the trap engages keeps their caret. The
 *    50ms timer overrode that click and dropped every keystroke after it into
 *    the panel's Close button.
 *  - **It only counts controls the operator can actually reach.** The selector
 *    below is a pure attribute match, so a `display:none` control matches it
 *    while real Tab skips it. Left in, it takes the `first` or `last` slot and
 *    the wrap silently stops firing at the true visible boundary.
 *
 * A fourth, from BUG-1764: **focus never rests inside a read-only preview
 * frame.** A document preview's PDF loads from a signed storage URL, i.e. a
 * cross-origin `<iframe>`, and once the operator clicks into it every keystroke
 * goes to the frame's own document — Escape never reaches `useTopmostEscape`'s
 * listener and the only way out is the mouse. No listener can see those keys,
 * so the trap takes focus back instead (see `reclaimFromFrame` below). The
 * cost is keyboard navigation inside the PDF; mouse scroll, the viewer's
 * toolbar and download all still work, because clicks and wheel events do not
 * need focus.
 *
 * **Opt-in, per frame** (`RECLAIM_FOCUS_FRAME_ATTR`). Not every iframe in an
 * overlay is a preview: the Finix card and bank-account fields in Receive
 * Payment are cross-origin iframes the operator must type into. A blanket
 * reclaim pulled focus out of the card-number field one task after every click,
 * and no payment could be taken. So an unmarked frame keeps its focus exactly
 * as before, and only a caller rendering a read-only preview marks its frame.
 *
 * @param ref - the element to trap focus within.
 * @param active - whether the trap is engaged. Pass `false` while the overlay
 * is closed AND while it has not yet mounted its node — a `ref` that is still
 * `null` when `active` first flips true is a no-op the effect cannot retry,
 * because nothing in its dependency list has changed by the time the node lands.
 */

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Put this attribute on a read-only preview `<iframe>` inside an overlay (a PDF,
 * an image) to have `useFocusTrap` take focus back when the operator clicks into
 * it, so Escape still closes the overlay. Never put it on a frame the operator
 * types into (hosted payment fields, an embedded editor). BUG-1764.
 */
export const RECLAIM_FOCUS_FRAME_ATTR = 'data-focus-trap-reclaim';

/**
 * Where focus lands when the trap engages.
 *
 * - `'first'` (default) — the first reachable control, or the node itself.
 * - `'container'` — the trapped node itself. For a phone sheet whose first
 *   control is a text field (BL-1998): focusing it on open raises the soft
 *   keyboard over half the form before the operator has read it. The node must
 *   be focusable (`tabIndex={-1}`); Tab still walks into the controls.
 */
export type FocusTrapInitialFocus = 'first' | 'container';

/** Elements that can receive keyboard focus. */
export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Whether `el` is genuinely reachable by Tab, walking ancestors up to `root`.
 *
 * `FOCUSABLE_SELECTOR` is a pure attribute selector, so it matches controls the
 * operator can never reach: the `<input type="file" class="hidden">` behind an
 * "Attach" button, a collapsed section, an `inert` subtree. One of those in the
 * list takes the `first` or `last` slot, the wrap stops firing at the boundary
 * the operator actually sees, and focus walks out of the overlay — on the
 * settlement full-screen modal it landed on a control in one of the closed
 * dialogs the page keeps mounted behind it. BUG-1386.
 *
 * Two implementation notes, both load-bearing:
 *
 *  - **It walks ancestors** rather than reading one computed style, because
 *    `display: none` on a PARENT does not show up in a child's computed value.
 *    `getComputedStyle` returns the computed value, not the used one, so a
 *    button inside a hidden `<div>` still reports its own `display`.
 *  - **It does not use the layout APIs.** `getClientRects().length` and
 *    `offsetParent` are the usual visibility tests and are correct in a browser,
 *    but jsdom has no layout engine and reports zero rects and a null
 *    `offsetParent` for EVERY element — visible ones included. Either one would
 *    filter every overlay down to empty under test, which is the state that let
 *    this defect through review the first time.
 *
 * `aria-hidden` counts as unreachable alongside `inert`: handing focus to
 * something a screen reader is told to ignore is the same failure in a
 * different modality.
 */
function isReachable(el: HTMLElement, root: HTMLElement): boolean {
  for (let n: HTMLElement | null = el; n != null; n = n.parentElement) {
    if (n.hasAttribute('inert') || n.getAttribute('aria-hidden') === 'true') return false;
    const { display, visibility } = getComputedStyle(n);
    if (display === 'none' || visibility === 'hidden' || visibility === 'collapse') return false;
    if (n === root) break;
  }
  return true;
}

/**
 * Trap focus inside `ref` while `active`, restoring the previously-focused
 * element when the trap disengages. Focuses the first focusable child (or the
 * node itself) on open — unless focus is already inside — and wraps Tab /
 * Shift+Tab at the boundaries.
 *
 * The restore lives in the effect's cleanup, which is what makes it correct for
 * both consumers: a skin that unmounts on close and a skin that merely flips
 * `active` to false both run the cleanup at the moment the modal goes away.
 *
 * @param ref - the element to trap focus within.
 * @param active - whether the trap is engaged.
 * @param initialFocus - where focus lands on engage; see {@link FocusTrapInitialFocus}.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  initialFocus: FocusTrapInitialFocus = 'first',
): void {
  // Read at engage time only, through a ref, so a value that changes while the
  // trap is open does NOT re-run the engage effect. A phone rotating across the
  // sheet tier flips it mid-edit; re-engaging would restore focus to the opener
  // behind the overlay, then move it to the first control or the container —
  // the operator loses the field they were typing in and the keyboard drops.
  const initialFocusRef = useRef(initialFocus);
  useEffect(() => {
    initialFocusRef.current = initialFocus;
  }, [initialFocus]);

  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const onEngage = document.activeElement as HTMLElement | null;
    const alreadyInside = onEngage != null && node.contains(onEngage);
    const getFocusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) =>
        isReachable(el, node),
      );

    // Respect a focus the operator has already placed inside the overlay. Only
    // an outside element is worth restoring to on close — restoring to a node
    // inside the overlay we are about to hide would strand focus.
    const previouslyFocused = alreadyInside ? null : onEngage;
    if (!alreadyInside) (initialFocusRef.current === 'container' ? node : (getFocusable()[0] ?? node)).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;

      // Focus sitting ON the container rather than in it — where the hand-off
      // leaves it when the overlay opens with a loading shell — is out of
      // bounds in both directions: the container precedes its children, so
      // Shift+Tab walks straight out to the page behind. Same for focus that
      // escaped entirely. Pull it back to the near edge. BUG-1386.
      if (current == null || current === node || !node.contains(current)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // An operator clicking into a marked preview `<iframe>` inside the overlay
    // moves focus into the frame's document: ours sees `activeElement` become
    // the iframe element and the window blur. Pull focus back to the container
    // so Escape and Tab keep reaching the overlay. Unmarked frames (payment
    // fields) keep focus. BUG-1764.
    //
    // The focus is deferred one task, and that is measured, not guessed: in
    // Chrome, a `focus()` called synchronously inside this blur updates
    // `activeElement` but leaves keyboard input routed to the frame, so Escape
    // was still lost. From the next task it takes. Scoped to frames inside
    // THIS node, so a page-level embed or another overlay's frame is left alone.
    let reclaimTimer: ReturnType<typeof setTimeout> | undefined;
    const reclaimFromFrame = () => {
      const frame = document.activeElement;
      if (!(frame instanceof HTMLIFrameElement) || !node.contains(frame)) return;
      if (!frame.hasAttribute(RECLAIM_FOCUS_FRAME_ATTR)) return;
      clearTimeout(reclaimTimer);
      reclaimTimer = setTimeout(() => {
        if (document.activeElement === frame) node.focus();
      }, 0);
    };

    node.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', reclaimFromFrame);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', reclaimFromFrame);
      clearTimeout(reclaimTimer);
      previouslyFocused?.focus?.();
    };
  }, [ref, active]);
}

/**
 * Focus management for a NON-MODAL panel (BL-2087): the half of
 * {@link useFocusTrap} a panel the page stays usable beside still needs.
 *
 * - On open, focus moves to the panel's first reachable control (or the panel
 *   itself) unless it is already inside — so a keyboard user does not have to
 *   Tab the whole page to reach a panel portaled to the end of `<body>`.
 * - There is NO Tab wrap: Tab walks out of the panel into the page, which is the
 *   point of a non-modal panel.
 * - On close, focus returns to the element that had it at open ONLY when focus
 *   is still inside the panel. A panel closed by a click behind it must leave
 *   focus on what the operator just clicked.
 *
 * @param ref - The panel element.
 * @param active - Whether the panel is open.
 */
export function useFocusOnOpen(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const onEngage = document.activeElement as HTMLElement | null;
    const alreadyInside = onEngage != null && node.contains(onEngage);
    if (!alreadyInside) {
      const first = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).find((el) =>
        isReachable(el, node),
      );
      (first ?? node).focus();
    }
    const opener = alreadyInside ? null : onEngage;

    // Whether focus is still the panel's, tracked because a panel that
    // UNMOUNTS on close (the bidder's portaled tray) takes the focused node with
    // it: by cleanup time `activeElement` is <body>, so "is focus inside?" would
    // always read no and Escape / ✕ would strand keyboard focus.
    //  - A pointer-down outside the panel, or a focus move to a real element
    //    outside it (Tab), is a departure: the click-out case keeps its focus.
    //  - A focusout with NO destination is not: Chrome fires exactly that when
    //    the focused node is removed with the panel, and an empty-area click is
    //    already recorded by the pointer-down.
    let focusInside = true;
    const onFocusIn = () => {
      focusInside = true;
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.relatedTarget instanceof Node) focusInside = node.contains(e.relatedTarget);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !node.contains(e.target)) focusInside = false;
    };
    node.addEventListener('focusin', onFocusIn);
    node.addEventListener('focusout', onFocusOut);
    document.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      node.removeEventListener('focusin', onFocusIn);
      node.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('pointerdown', onPointerDown, true);
      const current = document.activeElement;
      const stranded = current == null || current === document.body;
      if ((current != null && node.contains(current)) || (focusInside && stranded)) opener?.focus?.();
    };
  }, [ref, active]);
}
